import { mediaRotationHandoverEventsService } from '@/api/media-rotation-handover-events.service';
import type { ServerSentEventService } from '@/api/server-sent-event.service';
import type { CounterVideoWallHandoverEventDto } from '@carvia/ros-client-types';
import { computed, onUnmounted, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

/**
 * Subscribes the counter video wall to its station-scoped handover stream. Each
 * `handover.started` event (and keep-alive heartbeat) is forwarded to `onEvent`; the
 * caller owns the dedupe/queue/playback logic. Enabled only for the video wall device.
 */
export function useStationHandoverEvents(
  enabled: MaybeRefOrGetter<boolean>,
  onEvent: (event: CounterVideoWallHandoverEventDto) => void,
  stationSlug?: MaybeRefOrGetter<string | undefined>,
  stationAuthCode?: MaybeRefOrGetter<string | undefined>,
) {
  const isReconnecting = ref(false);

  let subscription: { unsubscribe: () => void } | null = null;
  let service: ServerSentEventService<CounterVideoWallHandoverEventDto> | null = null;

  const stop = () => {
    subscription?.unsubscribe();
    subscription = null;
    service?.close();
    service = null;
    isReconnecting.value = false;
  };

  watch(
    () => [toValue(enabled), toValue(stationSlug), toValue(stationAuthCode)] as const,
    ([isEnabled]) => {
      stop();
      if (!isEnabled) return;

      service = mediaRotationHandoverEventsService(toValue(stationSlug), toValue(stationAuthCode));
      subscription = service
        .open({
          onConnectionStateChange(state) {
            isReconnecting.value = state === 'reconnecting';
          },
        })
        .subscribe({
          next: (event) => onEvent(event),
        });
    },
    { immediate: true },
  );

  onUnmounted(stop);

  return {
    isReconnecting: computed(() => isReconnecting.value),
  };
}
